// Port of GaugeDetailView.swift + GaugeChartView.swift: the body of the gauge
// detail sheet. The surrounding DetailShell supplies the title and Done button.
// react-query's cache stands in for the two 50-entry static caches on iOS.
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import type { JSX, ReactNode } from 'react';
import { fetchNWPSGauge, fetchStageFlow } from '../../api/nwps';
import { fetchDailyStats, fetchGaugeData, percentileText } from '../../api/usgs';
import { Button, Card, IconButton, MetaRow, Pill, Segmented, Spinner, StatusMessage } from '../../components/ui/index';
import { categoryLabel } from '../../config/gauges';
import type { FloodCategory } from '../../config/gauges';
import type { Gauge } from '../../types/gauges';
import { SeriesChart } from './SeriesChart';
import { StageChart } from './StageChart';
import {
  TIME_RANGE_OPTIONS,
  buildStageFlowModel,
  buildThresholds,
  describeError,
  formatDrainageArea,
  isNotFoundError,
  sourceLink,
  usgsSiteNumber,
  visibleReadings,
} from './model';
import type { ChartTimeRange } from './model';
import './gauge-detail.css';

const STALE_MS = 5 * 60 * 1000;
const USGS_SOURCE = 'USGS National Water Information System';

/** One retry for transient NWPS failures; a 404 is final. */
const nwpsRetry = (failureCount: number, error: Error) => failureCount < 1 && !isNotFoundError(error);

export function GaugeDetailPanel({ gauge }: { gauge: Gauge }): JSX.Element {
  const id = gauge.id;

  // NWPS metadata (thresholds, usgsId, lid) and the hydrograph load together;
  // the chart never waits for the thresholds (GaugeChartViewModel.load).
  const nwps = useQuery({
    queryKey: ['nwps-gauge', id],
    queryFn: ({ signal }) => fetchNWPSGauge(id, signal),
    staleTime: STALE_MS,
    retry: nwpsRetry,
  });
  const flow = useQuery({
    queryKey: ['nwps-stageflow', id],
    queryFn: ({ signal }) => fetchStageFlow(id, signal),
    staleTime: STALE_MS,
    retry: nwpsRetry,
  });

  // USGS (7-day series and daily percentiles) keyed by the resolved site number.
  const usgsSite = usgsSiteNumber(nwps.data?.usgsId, id);
  const usgs = useQuery({
    queryKey: ['usgs-iv', usgsSite],
    queryFn: ({ signal }) => fetchGaugeData(usgsSite as string, signal),
    enabled: usgsSite != null,
    staleTime: STALE_MS,
    retry: false, // fetchGaugeData already retries 502/503/504
  });
  const stats = useQuery({
    queryKey: ['usgs-stats', usgsSite],
    queryFn: ({ signal }) => fetchDailyStats(usgsSite as string, new Date(), signal),
    enabled: usgsSite != null,
    staleTime: STALE_MS,
    retry: false,
  });

  const [range, setRange] = useState<ChartTimeRange>('all');
  const model = useMemo(() => buildStageFlowModel(flow.data), [flow.data]);
  const thresholds = useMemo(() => buildThresholds(nwps.data?.flood?.categories), [nwps.data]);
  // "Now" is pinned to the hydrograph's fetch time so the window and the rule stay put between renders.
  const nowMs = flow.dataUpdatedAt || Date.now();
  const now = useMemo(() => new Date(nowMs), [nowMs]);
  const visible = useMemo(() => visibleReadings(model.readings, range, now), [model, range, now]);

  const link = sourceLink(usgsSite, nwps.data?.lid ?? id);
  const refresh = () => {
    void nwps.refetch();
    void flow.refetch();
  };

  const latestHeight = usgs.data?.height.at(-1)?.value;
  const percentile = latestHeight != null && stats.data ? percentileText(latestHeight, stats.data) : null;

  return (
    <div className="gd-panel">
      {/* Header: name, site number pill, Observed / Forecast rows. */}
      <Card className="gd-header">
        <h3 className="gd-name">{gauge.name}</h3>
        <Pill color="var(--text-2)" outlined title="Site number">
          <span className="gd-pill-glyph" aria-hidden="true">
            #
          </span>
          <span className="gd-pill-text">{gauge.id}</span>
        </Pill>
        <div className="gd-severity">
          <SeverityRow title="Observed" icon={<EyeGlyph />} category={gauge.observationSeverity} />
          <SeverityRow title="Forecast" icon={<TrendGlyph />} category={gauge.forecastSeverity} />
        </div>
      </Card>

      {/* Stage chart (GaugeChartView). */}
      <Card className="gd-card">
        <div className="gd-chart-head">
          <h3 className="gd-chart-title">Stage ({model.unit}) vs. Time</h3>
          <IconButton label="Refresh stage data" onClick={refresh} disabled={flow.isFetching}>
            <RefreshGlyph />
          </IconButton>
        </div>
        {flow.isPending || (flow.isError && flow.isFetching) ? (
          <div className="gd-chart-state">
            <Spinner label={`Loading ${id}…`} />
          </div>
        ) : flow.isError ? (
          <div className="gd-chart-state">
            <StatusMessage
              action={
                <Button onClick={refresh} variant="default">
                  Retry
                </Button>
              }
            >
              {describeError(flow.error, 'NWPS', id)}
            </StatusMessage>
          </div>
        ) : (
          <>
            <div className={flow.isFetching ? 'gd-refetching' : undefined}>
              <StageChart
                visible={visible}
                thresholds={thresholds}
                unit={model.unit}
                now={now}
                emptyMessage={range === 'recent' ? 'No stage readings within 72 hours of now. Switch to All Data to see the full record.' : 'NWPS returned no stage readings for this gauge.'}
              />
            </div>
            <div className="gd-range">
              <Segmented options={TIME_RANGE_OPTIONS} value={range} onChange={setRange} label="Time Range" />
            </div>
          </>
        )}
        <a className="btn btn-default gd-source" href={link.url} target="_blank" rel="noopener noreferrer">
          <ExternalGlyph />
          <span>{link.label}</span>
        </a>
      </Card>

      {/* USGS: loading / error / discharge + water temperature. */}
      {usgsSite == null ? (
        nwps.isPending ? (
          <Spinner label="Loading USGS data…" />
        ) : (
          <p className="gd-note">No USGS site is linked to this gauge, so discharge and water temperature are unavailable.</p>
        )
      ) : usgs.isPending || (usgs.isError && usgs.isFetching) ? (
        <Spinner label="Loading USGS data…" />
      ) : usgs.isError ? (
        <UsgsErrorCard message={describeError(usgs.error, 'USGS', usgsSite)} onRetry={() => void usgs.refetch()} />
      ) : usgs.data.discharge.length === 0 && usgs.data.temperature.length === 0 ? (
        <p className="gd-note">USGS has no discharge or water temperature readings for site {usgsSite} in the last 7 days.</p>
      ) : (
        <>
          {usgs.data.discharge.length > 0 ? (
            <ChartCard title="Discharge" unit="cfs">
              <SeriesChart name="Discharge" points={usgs.data.discharge} unit="cfs" digits={0} color="var(--chart-discharge)" height={200} />
            </ChartCard>
          ) : null}
          {usgs.data.temperature.length > 0 ? (
            <ChartCard title="Water Temperature" unit="°C">
              <SeriesChart name="Water temperature" points={usgs.data.temperature} unit="°C" digits={1} color="var(--chart-temperature)" height={180} />
            </ChartCard>
          ) : null}
        </>
      )}

      {/* Site Details (metadataCard). */}
      <Card title="Site Details" className="gd-card">
        <div className="gd-rows">
          <MetaRow label="Site No" value={gauge.id} />
          {gauge.drainageAreaKm != null ? <MetaRow label="Drainage Area" value={formatDrainageArea(gauge.drainageAreaKm)} /> : null}
          {percentile ? <MetaRow label="Percentile" value={percentile} /> : null}
          <MetaRow label="Period" value="Last 7 days" />
          <MetaRow label="Source" value={USGS_SOURCE} />
        </div>
      </Card>
    </div>
  );
}

// MARK: - Building blocks

/** GaugeDetailView.severityRow: tinted icon, uppercase caption, category name or "Not available". */
function SeverityRow({ title, icon, category }: { title: string; icon: ReactNode; category: FloodCategory | null }) {
  return (
    <div className="gd-sev-row">
      <span className="gd-sev-icon" aria-hidden="true">
        {icon}
      </span>
      <div className="gd-sev-text">
        <span className="gd-sev-title">{title}</span>
        <span className={`gd-sev-value ${category == null ? 'is-muted' : ''}`}>{category == null ? 'Not available' : categoryLabel(category)}</span>
      </div>
    </div>
  );
}

/** GaugeDetailView.ChartCard: headline title with the unit as a caption. */
function ChartCard({ title, unit, children }: { title: string; unit: string; children: ReactNode }) {
  return (
    <Card className="gd-card">
      <div className="gd-chart-head">
        <h3 className="gd-chart-title">{title}</h3>
        <span className="gd-chart-unit">{unit}</span>
      </div>
      {children}
    </Card>
  );
}

/** GaugeDetailView.usgsErrorCard: inline notice in place of the USGS charts. */
function UsgsErrorCard({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <Card className="gd-card">
      <div className="gd-usgs-error" role="alert">
        <span className="gd-usgs-error-icon" aria-hidden="true">
          <WarningGlyph />
        </span>
        <div className="gd-usgs-error-body">
          <div className="gd-usgs-error-title">USGS data unavailable</div>
          <div className="gd-usgs-error-msg">{message}</div>
        </div>
        <IconButton label="Retry loading USGS data" onClick={onRetry}>
          <RefreshGlyph />
        </IconButton>
      </div>
    </Card>
  );
}

// Inline stand-ins for the SF Symbols the iOS view uses.

function Glyph({ children, size = 14 }: { children: ReactNode; size?: number }) {
  return (
    <svg
      className="gd-glyph"
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  );
}

function EyeGlyph() {
  return (
    <Glyph size={13}>
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" />
      <circle cx="12" cy="12" r="3" />
    </Glyph>
  );
}

function TrendGlyph() {
  return (
    <Glyph size={13}>
      <polyline points="3 17 9 11 13 15 21 7" />
      <polyline points="15 7 21 7 21 13" />
    </Glyph>
  );
}

function RefreshGlyph() {
  return (
    <Glyph size={16}>
      <path d="M21 12a9 9 0 1 1-2.64-6.36" />
      <polyline points="21 3 21 9 15 9" />
    </Glyph>
  );
}

function WarningGlyph() {
  return (
    <Glyph size={20}>
      <path d="M12 3.5 21.5 20H2.5L12 3.5z" />
      <path d="M12 9.5v5" />
      <circle cx="12" cy="17" r="0.6" fill="currentColor" />
    </Glyph>
  );
}

function ExternalGlyph() {
  return (
    <Glyph size={15}>
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
      <polyline points="15 3 21 3 21 9" />
      <line x1="10" y1="14" x2="21" y2="3" />
    </Glyph>
  );
}
